import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { unlockSharedAudio } from "@/utils/audioPlayer";

const VIDEO_URL = "/e7070498454f87fb53c46c395ba42471.mp4";
const GIF_URL = "/e7070498454f87fb53c46c395ba42471.gif";
const POSTER_IMG = "/image.png";
// 视频自动播放超时 (ms) — 超时后切回 GIF
const VIDEO_AUTOPLAY_TIMEOUT = 1500;
// GIF/动画展示时间 (ms)，之后显示按钮
const BTN_SHOW_DELAY = 6000;

export default function SplashPage() {
  const navigate = useNavigate();
  const [showBtn, setShowBtn] = useState(false);
  const [requesting, setRequesting] = useState(false);
  // true = 视频在播放，false/undefined = 用 GIF
  const [videoActive, setVideoActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const btnTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const videoTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  /** 尝试播放视频 */
  const tryPlayVideo = useCallback(async (video: HTMLVideoElement): Promise<boolean> => {
    try {
      video.muted = true;
      await video.play();
      return true;
    } catch {
      return false;
    }
  }, []);

  /** 停止并显示按钮 */
  const settle = useCallback(() => {
    if (btnTimerRef.current) clearTimeout(btnTimerRef.current);
    if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
    setShowBtn(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let settled = false;
    let videoSucceeded = false;
    const doSettle = () => {
      if (settled) return;
      settled = true;
      settle();
    };

    // ════════════════════════════════════════
    // 策略：GIF 默认展示 + 后台静默尝试视频自动播放
    // ════════════════════════════════════════

    // ── 1. 立即尝试 autoplay ──
    tryPlayVideo(video).then((ok) => {
      if (ok && !settled) {
        videoSucceeded = true;
        setVideoActive(true);
      }
    });

    // ── 2. canplay 后再试一次（数据就绪时）──
    const onCanPlay = () => {
      if (videoSucceeded || settled) return;
      tryPlayVideo(video).then((ok) => {
        if (ok && !settled) {
          videoSucceeded = true;
          setVideoActive(true);
        }
      });
    };
    video.addEventListener("canplay", onCanPlay);

    // ── 3. 用户交互时也尝试（如果之前没成功）──
    const onInteraction = () => {
      if (videoSucceeded || settled) return;
      tryPlayVideo(video).then((ok) => {
        if (ok && !settled) {
          videoSucceeded = true;
          setVideoActive(true);
        }
      });
    };
    document.addEventListener("touchstart", onInteraction, { passive: true });
    document.addEventListener("click", onInteraction);

    // ── 4. 微信 JSBridge ──
    const tryWx = () => {
      const wx = (window as any).WeixinJSBridge;
      if (!wx?.invoke) return;
      wx.invoke("getNetworkType", {}, () => {
        if (videoSucceeded || settled) return;
        tryPlayVideo(video).then((ok) => {
          if (ok && !settled) {
            videoSucceeded = true;
            setVideoActive(true);
          }
        });
      });
    };
    if ((window as any)?.WeixinJSBridge?.invoke) {
      tryWx();
    } else {
      (window as any).onWeixinJSBridgeReady = tryWx;
    }

    // ── 5. 超时兜底：无论视频是否成功都显示按钮 ──
    btnTimerRef.current = window.setTimeout(() => {
      if (!settled) doSettle();
    }, BTN_SHOW_DELAY);

    // ── 视频结束检测（仅当视频在播放时）──
    const onEnded = () => {
      console.log("[Splash] 视频播放完毕");
      doSettle();
    };
    video.addEventListener("ended", onEnded);

    let nearEndTriggered = false;
    const onTimeUpdate = () => {
      if (!videoSucceeded || nearEndTriggered || !video.duration || settled) return;
      if (video.currentTime >= video.duration * 0.9) {
        nearEndTriggered = true;
        doSettle();
      }
    };
    video.addEventListener("timeupdate", onTimeUpdate);

    video.addEventListener("error", () => {
      console.warn("[Splash] 视频加载错误，使用 GIF 展示");
    }, { once: true });

    // 清理
    return () => {
      video.removeEventListener("ended", onEnded);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("canplay", onCanPlay);
      document.removeEventListener("touchstart", onInteraction);
      document.removeEventListener("click", onInteraction);
      if (btnTimerRef.current) clearTimeout(btnTimerRef.current);
      if (videoTimeoutRef.current) clearTimeout(videoTimeoutRef.current);
    };
  }, [tryPlayVideo, settle]);

  /** 用户点击按钮 */
  const handleStart = async () => {
    setRequesting(true);

    // ★ 利用用户点击手势提前解锁音频子系统（AudioContext + HTMLAudioElement）
    //    确保进入 ai-voice 页面后 SSE 推送的 TTS 能自动播放，无需用户再点"播报"按钮
    unlockSharedAudio();

    const video = videoRef.current;
    if (video) {
      try { await video.play(); } catch { /* ignore */ }
    }

    // 检查浏览器是否支持 mediaDevices API
    if (!navigator.mediaDevices?.getUserMedia) {
      console.warn("[Splash] 浏览器不支持 getUserMedia，跳过麦克风权限预检");
      navigate("/home");
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        navigate("/home");
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[Splash] 麦克风权限被拒绝:", msg);
        navigate("/home");
      }
    }
    setRequesting(false);
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden bg-white">
      {/* ══ GIF 背景（始终存在，默认显示）══ */}
      <img
        src={GIF_URL}
        alt=""
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoActive ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      />

      {/* ══ 视频背景（成功自动播放后才显示）══ */}
      <video
        ref={videoRef}
        src={VIDEO_URL}
        poster={POSTER_IMG}
        autoPlay
        muted
        playsInline
        preload="auto"
        webkit-playsinline="true"
        x5-video-player-type="h5-page"
        x5-video-player-fullscreen="false"
        x5-video-orientation="portrait"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoActive ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        style={{ background: "#fff" }}
      />

      {/* ══ 底部：立即开始按钮 ══ */}
      <div
        className="absolute flex flex-col items-center gap-4 z-10"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)",
          opacity: showBtn ? 1 : 0,
          transform: showBtn ? "translateY(0)" : "translateY(14px)",
          transition: "all 0.6s ease 0.2s",
          pointerEvents: showBtn ? "auto" : "none",
        }}
      >
        <button
          onClick={handleStart}
          disabled={requesting}
          className="flex items-center gap-2.5 px-8 py-3.5 rounded-2xl cursor-pointer active:scale-95 transition-all whitespace-nowrap"
          style={{
            background: "linear-gradient(135deg, #00C851, #00A043)",
            boxShadow: "0 8px 32px rgba(0,200,81,0.35)",
            border: "none",
          }}
        >
          {requesting ? (
            <>
              <i
                className="ri-loader-4-line animate-spin"
                style={{ color: "#fff", fontSize: 18 }}
              />
              <span className="text-sm font-semibold" style={{ color: "#fff" }}>
                请求录音权限...
              </span>
            </>
          ) : (
            <>
              <i className="ri-mic-fill" style={{ color: "#fff", fontSize: 17 }} />
              <span
                className="text-base font-semibold"
                style={{ color: "#fff", letterSpacing: "0.05em" }}
              >
                立即开始
              </span>
            </>
          )}
        </button>
        <p className="text-[11px]" style={{ color: "rgba(0,0,0,0.3)" }}>
          点击后将请求麦克风权限以启用语音对讲
        </p>
      </div>

    </div>
  );
}
